import React from 'react';
import { PackageCondition, PackageStatus } from '@/services/api/packages';

// Item status labels — Status Model v2 (docs/09-FE-CHANGES.md §1). `Delivered`
// became `Completed` and `Returned` became `Returned to Hub`; `Damaged` is gone
// from here entirely (it's a condition now — see ConditionBadge below).
export const STATUS_LABELS: Record<PackageStatus, string> = {
  CREATED: 'Created',
  HUB_SCANNED: 'At Hub',
  DISPATCHED: 'Dispatched',
  HANDOVER_SCANNED: 'Handover Scanned',
  IN_DELIVERY: 'In Delivery',
  IN_RETURN: 'In Return',
  COMPLETED: 'Completed',
  RETURNED_TO_HUB: 'Returned to Hub',
  RETURNED_TO_CLIENT: 'Returned to Client',
  LOST: 'Lost',
  CANCELLED: 'Cancelled',
};

// Success tones are reserved for the delivered end-state; the hub/rider legs in
// between read as "in progress" (amber/violet).
export const STATUS_COLORS: Record<PackageStatus, string> = {
  CREATED: 'bg-feature-lighter text-feature-dark',
  HUB_SCANNED: 'bg-information-lighter text-information-dark',
  DISPATCHED: 'bg-away-lighter text-away-dark',
  HANDOVER_SCANNED: 'bg-away-lighter text-away-dark',
  IN_DELIVERY: 'bg-highlighted-lighter text-highlighted-dark',
  IN_RETURN: 'bg-warning-lighter text-warning-dark',
  COMPLETED: 'bg-success-lighter text-success-dark',
  RETURNED_TO_HUB: 'bg-warning-lighter text-warning-dark',
  RETURNED_TO_CLIENT: 'bg-warning-lighter text-warning-dark',
  LOST: 'bg-error-lighter text-error-dark',
  CANCELLED: 'bg-faded-lighter text-faded-dark',
};

// Unmapped values (a status the gateway added before this list caught up) still
// read as words rather than a raw enum.
const toTitleCase = (status: string) =>
  status
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

export const getPackageStatusLabel = (status: string): string =>
  STATUS_LABELS[status as PackageStatus] ?? toTitleCase(status || 'Unknown');

export const PackageStatusBadge: React.FC<{
  status: PackageStatus | string;
  className?: string;
}> = ({ status, className = '' }) => (
  <span
    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
      STATUS_COLORS[status as PackageStatus] ?? 'bg-bg-weak-50 text-text-sub-600'
    } ${className}`.trim()}
  >
    {getPackageStatusLabel(status)}
  </span>
);

/**
 * Damage badge (Status Model v2 §4). `condition` runs alongside `status`, so a
 * damaged item still shows its real stage — this sits *next to* the status
 * badge, never in place of it. Renders nothing unless the item is DAMAGED.
 */
export const ConditionBadge: React.FC<{
  condition?: PackageCondition | string | null;
  className?: string;
}> = ({ condition, className = '' }) => {
  if (condition !== 'DAMAGED') return null;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full bg-error-lighter px-2 py-0.5 text-xs font-medium text-error-dark ${className}`.trim()}
      title="Barang rusak — pengiriman tetap berjalan"
    >
      <i className="ri-alert-line text-[13px]" />
      Rusak
    </span>
  );
};
